import React from 'react';
import { MapPin, Clock, Phone, Mail, CheckCircle2, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEOHelmet from '../../components/SEOHelmet';
import { companyInfo } from '../../mockData';

const JoshuaTreePage = () => {
  return (
    <div style={{ backgroundColor: 'var(--bg-page)', minHeight: '100vh' }}>
      <SEOHelmet 
        title="Process Server Joshua Tree CA | Legal Document Services | Legal Document Specialist"
        description="Joshua Tree National Park gateway process server. Specialized service for tourism businesses, artist communities, and eco-tourism operators. Expertise in creative community and environmental business legal needs."
        keywords="process server Joshua Tree CA, legal document services Joshua Tree, court filing Joshua Tree, skip tracing Joshua Tree, San Bernardino County process server"
        canonical="https://legaldocspecialist.com/cities/joshua-tree"
      />
      <Header />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)'
            }}>
              Process Server Joshua Tree, California
            </h1>
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}>
              Professional legal document services in Joshua Tree, CA. Registered and bonded process servers serving the gateway to Joshua Tree National Park and Hi-Desert tourism community.
            </p>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Now
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services & Tourism Focus */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Joshua Tree Legal Document Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Specialized legal support services for the National Park gateway community and Hi-Desert tourism industry.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
            {/* Services */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <CheckCircle2 size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                National Park Gateway Services
              </h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {[
                  'Process Serving & Legal Notices',
                  'Court Document Filing',
                  'County Recording Services',
                  'Document Retrieval & Research',
                  'Skip Tracing & Locate Services'
                ].map((service, index) => (
                  <li key={service} style={{ 
                    padding: '0.75rem 0',
                    borderBottom: index < 4 ? '1px solid var(--border-light)' : 'none',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <CheckCircle2 size={16} color="var(--brand-primary)" style={{ marginRight: '0.75rem' }} />
                    <span style={{ color: 'var(--text-primary)' }}>{service}</span>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
                Specialized service for tourism industry and National Park area legal matters.
              </p>
            </div>

            {/* National Park Community */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center' }}>
                <Mountain size={24} color="var(--brand-primary)" style={{ marginRight: '0.5rem' }} />
                National Park Community Expertise
              </h3>
              <div style={{ color: 'var(--text-primary)', lineHeight: '1.6' }}>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Joshua Tree National Park Gateway</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Expert service for tourism and outdoor recreation industry</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Artist & Creative Community</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Familiar with the unique creative and artistic community</span>
                </p>
                <p style={{ marginBottom: '1rem' }}>
                  <strong>Desert Tourism Hub</strong><br />
                  <span style={{ color: 'var(--text-secondary)' }}>Specialized service for hospitality and tourism businesses</span>
                </p>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  Understanding of Joshua Tree's unique blend of tourism, arts, and desert community dynamics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tourism & Community Focus */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Why Choose Us for Joshua Tree?
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
              National Park expertise meets local knowledge. We understand Joshua Tree's unique tourism industry and creative community needs.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '2rem' }}>
            {[
              {
                icon: <Mountain size={32} />,
                title: 'National Park Expertise',
                description: 'Specialized experience serving tourism businesses and National Park area legal matters.'
              },
              {
                icon: <Clock size={32} />,
                title: 'Tourism Season Ready',
                description: 'Flexible service levels to accommodate seasonal tourism fluctuations and visitor needs.'
              },
              {
                icon: <CheckCircle2 size={32} />,
                title: 'Creative Community Focus',
                description: 'Registered, bonded, and experienced with the unique needs of artists and creative professionals.'
              }
            ].map((feature) => (
              <div key={feature.title} style={{
                backgroundColor: 'var(--bg-card)',
                padding: '2rem',
                borderRadius: '12px',
                border: '1px solid var(--border-medium)',
                textAlign: 'center'
              }}>
                <div style={{ color: 'var(--brand-primary)', marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}>
                  {feature.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '1rem' }}>{feature.title}</h3>
                <p className="body-text" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Coverage */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Joshua Tree Service Coverage
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Serving Joshua Tree and surrounding Hi-Desert communities from the gateway to Joshua Tree National Park.
            </p>
          </div>

          <div style={{
            backgroundColor: 'var(--bg-page)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
              Hi-Desert Communities Served from Joshua Tree Gateway
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'Joshua Tree', 'Twentynine Palms', 'Yucca Valley', 'Morongo Valley',
                'Pioneertown', 'Landers', 'Wonder Valley', 'Flamingo Heights',
                'Desert Center', 'Amboy', 'Kelso', 'Baker'
              ].map((city, index) => (
              <div key={city} style={{
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? 'var(--brand-primary)' : 'var(--bg-card)',
                  borderRadius: '6px',
                  border: '1px solid var(--border-light)'
                }}>
                  <span style={{ 
                    color: index === 0 ? 'white' : 'var(--text-primary)',
                    fontWeight: index === 0 ? '600' : 'normal'
                  }}>{city}</span>
                </div>
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Complete coverage of Mojave Desert and Hi-Desert region, including National Park area communities.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{ backgroundColor: 'var(--bg-page)', padding: '80px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              Contact Us for Joshua Tree Services
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
              Ready to get started? Contact Legal Document Specialist for professional service in Joshua Tree and the National Park area.
            </p>
          </div>

          <div style={{
            maxWidth: '600px',
            margin: '0 auto',
            backgroundColor: 'var(--bg-card)',
            padding: '2rem',
            borderRadius: '12px',
            border: '1px solid var(--border-medium)',
            textAlign: 'center'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <Phone size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '1.2rem' }}>{companyInfo.phone}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Monday-Friday 9AM-5PM PST</div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <Mail size={24} color="var(--brand-primary)" style={{ marginBottom: '0.5rem' }} />
              <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{companyInfo.email}</div>
              <div style={{ color: 'var(--text-secondary)' }}>Tourism industry support available</div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '2rem' }}>
              <Link to="/place-order" className="btn-primary" style={{ textDecoration: 'none' }}>
                Place Order Online
              </Link>
              <Link to="/quote" className="btn-secondary" style={{ textDecoration: 'none' }}>
                Request Quote
              </Link>
            </div>

            <div style={{ 
              backgroundColor: 'var(--bg-page)', 
              padding: '1rem', 
              borderRadius: '6px',
              border: '1px solid var(--border-light)'
            }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                <strong>Serving the National Park Gateway</strong><br />
                Tourism Specialists • Creative Community • Desert Experts
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JoshuaTreePage;